import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppBar from '../components/AppBar'
import SubmitButton from '../components/SubmitButton'
import LoadingSpinner from '../components/LoadingSpinner'
import { universityApi, UniversityMembership } from '../api/university'
import { toastUtil } from '../utils/toast'
import { UNIVERSITY_BADGE, shortUniversityName } from '../constants/university'

/**
 * 다시 들어가기 — 소속을 해제했던 사람 (Figma `다시 들어가기` 6433:8804, 2026-11-04).
 *
 * 홈 대학교 아이콘 → 학교 고르기 화면이 해제한 사람을 여기로 보낸다.
 * '참여하기'는 메일 인증 없이 예전 학교로 돌려놓는다(서버 /university/rejoin).
 * 다른 학교로 바꾸는 길은 두지 않았다 — 한 사람 한 학교, 바꿔야 하면 문의로 받는다.
 *
 * 배치는 인증 완료 화면(UniversityDone)과 같다. Figma 에는 뒤로가기가 없지만
 * 참여하지 않을 사람이 나갈 길이 있어야 해서 왼쪽 위 뒤로가기만 둔다.
 */
const UniversityRejoin: React.FC = () => {
  const navigate = useNavigate()
  const [membership, setMembership] = useState<UniversityMembership | null>(null)
  const [joining, setJoining] = useState(false)

  useEffect(() => {
    let alive = true
    universityApi
      .getMembership()
      .then((m) => {
        if (!alive) return
        if (!m) navigate('/university', { replace: true })
        else if (!m.leftAt) navigate('/university/group', { replace: true })
        else setMembership(m)
      })
      .catch(() => {
        if (alive) navigate('/university', { replace: true })
      })
    return () => {
      alive = false
    }
  }, [navigate])

  const join = async () => {
    if (joining) return
    setJoining(true)
    try {
      await universityApi.rejoin()
      navigate('/university/group', { replace: true })
    } catch (e: any) {
      toastUtil.error(e?.response?.data?.message ?? '잠시 후 다시 시도해주세요')
      setJoining(false)
    }
  }

  if (!membership) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-backgroundColorBg2">
        <LoadingSpinner />
      </div>
    )
  }

  const u = membership.university
  const badge = UNIVERSITY_BADGE[u.key] ?? u.logoPath

  return (
    <div className="w-full h-full flex flex-col bg-backgroundColorBg2">
      <AppBar />
      <div className="app-container flex-1 flex flex-col pb-32px">
        <div className="flex-1 flex flex-col items-center justify-center px-20px">
          {badge && (
            <img src={badge} alt={`${u.name} 로고`} className="w-168px h-168px object-contain" />
          )}
          <p className="mt-46px font-pretendard font-black text-24px leading-135% tracking-[-0.24px] text-brown-80%-main text-center">
            {shortUniversityName(u.name)} 그룹에 다시 들어갈까요?
          </p>
          <p className="mt-10px font-pretendard font-regular text-15px leading-150% tracking-[-0.15px] text-textColorText2 text-center">
            이미 인증을 마쳤어요
          </p>
        </div>

        <div className="w-full px-20px">
          <SubmitButton text="참여하기" onSubmit={join} loading={joining} />
        </div>
      </div>
    </div>
  )
}

export default UniversityRejoin
